import {
    createContext,
    useState,
    useMemo,
    useCallback,
    useEffect,
    useRef,
    type ReactNode,
} from 'react'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { Agent } from '@atproto/api'
import { VERIFICATION_STORAGE_KEY, FALLBACK_TRUSTED_VERIFIERS } from '@/constants/verification'
import { fetchDefaultVerifiers } from '@/services/verificationService'
import {
    fetchTrustedVerifiers,
    saveTrustedVerifiers,
    type TrustedVerifiersData,
} from '@/services/trustedVerifiersService'
import { useAuth } from '@/hooks/useAuth'
import type { VerificationPreferences, TrustedVerifier } from '@/types/verification'

/** Returns the localStorage key scoped to the given account DID, falling back to the global key for unauthenticated users. */
function preferencesStorageKey(did?: string): string {
    return did ? `${VERIFICATION_STORAGE_KEY}:${did}` : VERIFICATION_STORAGE_KEY
}

export interface VerificationPreferencesContextType {
    preferences: VerificationPreferences
    setEnabled: (enabled: boolean) => void
    setHideAll: (hideAll: boolean) => void
    addVerifier: (verifier: TrustedVerifier) => void
    removeVerifier: (did: string) => void
    resetToDefaults: () => void
}

export const VerificationPreferencesContext = createContext<
    VerificationPreferencesContextType | undefined
>(undefined)

function getStoredPreferences(did?: string): VerificationPreferences {
    try {
        const stored = localStorage.getItem(preferencesStorageKey(did))
        if (stored) {
            const parsed = JSON.parse(stored)
            if (typeof parsed.enabled === 'boolean' && Array.isArray(parsed.trustedVerifiers)) {
                return {
                    ...parsed,
                    hideAll: typeof parsed.hideAll === 'boolean' ? parsed.hideAll : false,
                    trustedVerifiers: parsed.trustedVerifiers.map((v: TrustedVerifier) => ({
                        ...v,
                        handle: v.handle ?? v.label,
                    })),
                }
            }
        }
    } catch {
        // localStorage unavailable or corrupted
    }
    return { enabled: false, hideAll: false, trustedVerifiers: FALLBACK_TRUSTED_VERIFIERS }
}

function persistPreferences(prefs: VerificationPreferences, did?: string) {
    try {
        localStorage.setItem(preferencesStorageKey(did), JSON.stringify(prefs))
    } catch {
        // localStorage unavailable
    }
}

export function VerificationPreferencesProvider({ children }: { children: ReactNode }) {
    const queryClient = useQueryClient()
    const { session } = useAuth()
    const currentDid = session?.did
    const agent = useMemo(() => (session ? new Agent(session) : null), [session])
    const [preferences, setPreferences] = useState<VerificationPreferences>(() =>
        getStoredPreferences(currentDid)
    )

    // Reset preferences when the active account changes so each account starts from
    // its own localStorage snapshot (server data will then override via the query below).
    const prevDidRef = useRef<string | undefined>(currentDid)
    useEffect(() => {
        if (prevDidRef.current === currentDid) return
        prevDidRef.current = currentDid
        setPreferences(getStoredPreferences(currentDid))
    }, [currentDid])

    // Fetch user's trusted verifiers from XRPC server (authenticated users only)
    const { data: serverData } = useQuery({
        queryKey: ['trustedVerifiers', currentDid],
        queryFn: () => fetchTrustedVerifiers(agent!),
        enabled: !!agent,
        staleTime: 30 * 60 * 1000, // 30 minutes
        retry: false,
    })

    const { data: apiVerifiers } = useQuery({
        queryKey: ['defaultVerifiers'],
        queryFn: fetchDefaultVerifiers,
        staleTime: 60 * 60 * 1000, // 1 hour
        retry: false, // fall back to hardcoded list on failure
    })

    // Server is authoritative: when server data arrives, overwrite all fields and cache to localStorage.
    // serverData is scoped to currentDid via queryKey: ['trustedVerifiers', currentDid],
    // so a stale account's response will not overwrite the newly-active account's data.
    useEffect(() => {
        if (!serverData) return
        const next: VerificationPreferences = {
            enabled: serverData.enabled,
            hideAll: serverData.hideAll,
            trustedVerifiers:
                serverData.verifiers.length > 0
                    ? serverData.verifiers
                    : (apiVerifiers ?? FALLBACK_TRUSTED_VERIFIERS),
        }
        setPreferences(next)
        persistPreferences(next, currentDid)
    }, [serverData, currentDid, apiVerifiers])

    // When API verifiers arrive, update stored list for non-customized users (unauthenticated fallback)
    useEffect(() => {
        if (!apiVerifiers || apiVerifiers.length === 0) return
        if (agent) return // Server is source of truth for authenticated users

        setPreferences(prev => {
            const next = { ...prev, trustedVerifiers: apiVerifiers }
            persistPreferences(next, currentDid)
            return next
        })
    }, [apiVerifiers, agent, currentDid])

    const invalidateVerificationQueries = useCallback(() => {
        void queryClient.invalidateQueries({ queryKey: ['customVerification'] })
    }, [queryClient])

    const setEnabled = useCallback(
        (enabled: boolean) => {
            setPreferences(prev => {
                const next = { ...prev, enabled }
                persistPreferences(next, currentDid)
                return next
            })
            // Side effects outside updater: React 18 strict mode invokes updaters twice in dev.
            if (agent) {
                void saveTrustedVerifiers(agent, preferences.trustedVerifiers, {
                    enabled,
                    hideAll: preferences.hideAll,
                })
                queryClient.setQueryData<TrustedVerifiersData | null>(
                    ['trustedVerifiers', currentDid],
                    prev => (prev ? { ...prev, enabled } : null)
                )
            }
            invalidateVerificationQueries()
        },
        [invalidateVerificationQueries, agent, preferences, currentDid, queryClient]
    )

    const setHideAll = useCallback(
        (hideAll: boolean) => {
            setPreferences(prev => {
                const next = { ...prev, hideAll }
                persistPreferences(next, currentDid)
                return next
            })
            // Side effects outside updater: React 18 strict mode invokes updaters twice in dev.
            if (agent) {
                void saveTrustedVerifiers(agent, preferences.trustedVerifiers, {
                    enabled: preferences.enabled,
                    hideAll,
                })
                queryClient.setQueryData<TrustedVerifiersData | null>(
                    ['trustedVerifiers', currentDid],
                    prev => (prev ? { ...prev, hideAll } : null)
                )
            }
            if (!hideAll) {
                invalidateVerificationQueries()
            }
        },
        [invalidateVerificationQueries, agent, preferences, currentDid, queryClient]
    )

    const addVerifier = useCallback(
        (verifier: TrustedVerifier) => {
            if (preferences.trustedVerifiers.some(v => v.did === verifier.did)) return
            const nextVerifiers = [...preferences.trustedVerifiers, verifier]
            setPreferences(prev => {
                const next = { ...prev, trustedVerifiers: nextVerifiers }
                persistPreferences(next, currentDid)
                return next
            })
            if (agent) {
                void saveTrustedVerifiers(agent, nextVerifiers, {
                    enabled: preferences.enabled,
                    hideAll: preferences.hideAll,
                })
                queryClient.setQueryData<TrustedVerifiersData | null>(
                    ['trustedVerifiers', currentDid],
                    prev => (prev ? { ...prev, verifiers: nextVerifiers } : null)
                )
            }
            invalidateVerificationQueries()
        },
        [invalidateVerificationQueries, agent, currentDid, preferences, queryClient]
    )

    const removeVerifier = useCallback(
        (did: string) => {
            const nextVerifiers = preferences.trustedVerifiers.filter(v => v.did !== did)
            setPreferences(prev => {
                const next = { ...prev, trustedVerifiers: nextVerifiers }
                persistPreferences(next, currentDid)
                return next
            })
            if (agent) {
                void saveTrustedVerifiers(agent, nextVerifiers, {
                    enabled: preferences.enabled,
                    hideAll: preferences.hideAll,
                })
                queryClient.setQueryData<TrustedVerifiersData | null>(
                    ['trustedVerifiers', currentDid],
                    prev => (prev ? { ...prev, verifiers: nextVerifiers } : null)
                )
            }
            invalidateVerificationQueries()
        },
        [invalidateVerificationQueries, agent, currentDid, preferences, queryClient]
    )

    const resetToDefaults = useCallback(() => {
        const defaults = apiVerifiers ?? FALLBACK_TRUSTED_VERIFIERS
        setPreferences(prev => {
            const next: VerificationPreferences = {
                enabled: prev.enabled,
                hideAll: prev.hideAll,
                trustedVerifiers: defaults,
            }
            persistPreferences(next, currentDid)
            return next
        })
        // Side effects outside updater: React 18 strict mode invokes updaters twice in dev.
        if (agent) {
            void saveTrustedVerifiers(agent, defaults, {
                enabled: preferences.enabled,
                hideAll: preferences.hideAll,
            })
            queryClient.setQueryData<TrustedVerifiersData | null>(
                ['trustedVerifiers', currentDid],
                prev => (prev ? { ...prev, verifiers: defaults } : null)
            )
        }
        invalidateVerificationQueries()
    }, [apiVerifiers, invalidateVerificationQueries, agent, currentDid, preferences, queryClient])

    const value = useMemo(
        () => ({
            preferences,
            setEnabled,
            setHideAll,
            addVerifier,
            removeVerifier,
            resetToDefaults,
        }),
        [preferences, setEnabled, setHideAll, addVerifier, removeVerifier, resetToDefaults]
    )

    return (
        <VerificationPreferencesContext.Provider value={value}>
            {children}
        </VerificationPreferencesContext.Provider>
    )
}
